import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { alpha, useColorScheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { MouseEvent, ReactNode, useCallback, useState } from "react";
import { FaCrown } from "react-icons/fa";
import { IoChatbubbles, IoGift, IoNotifications } from "react-icons/io5";
import { MdChevronRight } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import AppIcon from "@/components/Core/AppIcon";
import { HEADER_BUTTON_SIZE, headerBadgeSx } from "@/components/Core/HeaderIconButton";
import Translations from "@/components/Core/Translations";
import { UserAvatar } from "@/components/Core/UserAvatar";
import { DEFAULT_AVATAR_SRC } from "@/components/Core/UserAvatar/data";
import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { getMediaUrl, routers } from "@/configs";
import {
  useChatRoomGameContext,
  useDeviceType,
  useDialog,
  useIsLoggedIn,
  useLogout,
  useNotifications,
  useTranslate,
  useUser,
} from "@/hooks";
import { getDailySpinStatus, getMyVipStanding } from "@/services";
import { colors } from "@/theme";
import { dailySpinStatusKey } from "@/views/Others/DailySpin/data";

import { PROFILE_ITEMS, VIP_STANDING_QUERY_KEY } from "./data";
import { Profile } from "./type";

const SKY = "#5BC8FF";
const CROWN = "#FDC445";
/** The crown's gold as ink on a pale card, where the gold itself is too light to read. */
const CROWN_INK = "#9A6700";

interface MenuRowProps {
  icon: ReactNode;
  label: ReactNode;
  onClick: () => void;
  /** Signing out: set apart in red, with no chevron - it goes nowhere. */
  danger?: boolean;
  /** Shown in place of the chevron, for a row that switches rather than opens. */
  trailing?: ReactNode;
}

/** One line of the menu: an icon on a tile, what it opens, and a chevron. */
const MenuRow = ({ icon, label, onClick, danger = false, trailing }: MenuRowProps) => {
  const tone = danger ? colors.red : colors.prime;

  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        width: "100%",
        justifyContent: "flex-start",
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: "10px",
        fontFamily: "inherit",
        textAlign: "left",
        color: danger ? colors.red : theme.palette.text.primary,
        transition: "background-color 160ms ease",
        "&:hover": { backgroundColor: alpha(tone, 0.1) },
        "&:hover .menu-row-tile": { color: tone, backgroundColor: alpha(tone, 0.16) },
        "&:focus-visible": { outline: `2px solid ${SKY}`, outlineOffset: -2 },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      })}
    >
      <Box
        className="menu-row-tile"
        sx={(theme) => ({
          flexShrink: 0,
          width: 34,
          height: 34,
          display: "grid",
          placeItems: "center",
          borderRadius: "10px",
          color: danger ? colors.red : theme.palette.text.secondary,
          backgroundColor: alpha(tone, 0.08),
          transition: "background-color 160ms ease, color 160ms ease",
          "& .app-icon": { color: "inherit" },
        })}
      >
        {icon}
      </Box>
      <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600, color: "inherit" }} noWrap>
        {label}
      </Typography>
      {trailing ??
        (!danger && (
          <MdChevronRight aria-hidden size={20} style={{ flexShrink: 0, opacity: 0.5 }} />
        ))}
    </ButtonBase>
  );
};

/**
 * The avatar in the header, and the menu it opens.
 *
 * The menu was a two-by-two grid of bordered cells under the player's name:
 * four destinations of equal weight, signing out among them and the same size
 * as the rest. It is a list now - where the player is in the VIP club first,
 * as a card that goes there, then the places to go as rows, and signing out
 * last, apart and in red.
 */
export default function UserProfile() {
  const user = useUser();
  const navigate = useNavigate();
  const logout = useLogout();
  const t = useTranslate();

  const { data: standing } = useQuery({
    queryKey: VIP_STANDING_QUERY_KEY,
    queryFn: getMyVipStanding,
    enabled: Boolean(user),
  });
  const avatar = user?.avatarUrl;
  const { mode, systemMode, setMode } = useColorScheme();
  const dark = (systemMode || mode) === "dark";
  const isLoggedIn = useIsLoggedIn();
  const { openDialog } = useDialog();
  // Same query as the wheel's teaser, so the row shares its cache.
  const { data: dailySpin } = useQuery({
    queryKey: dailySpinStatusKey(isLoggedIn),
    queryFn: getDailySpinStatus,
    staleTime: 60 * 1000,
  });
  const { isMobile } = useDeviceType();
  const { unreadCount } = useNotifications();
  const { toggle: toggleChatRoom } = useChatRoomGameContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const go = useCallback(
    (link: string) => {
      navigate(link);
      handleClose();
    },
    [navigate, handleClose],
  );

  const handleOpenChatRoom = useCallback(() => {
    toggleChatRoom();
    handleClose();
  }, [toggleChatRoom, handleClose]);

  const handleOpenDailySpin = useCallback(() => {
    openDialog("daily-spin");
    handleClose();
  }, [openDialog, handleClose]);

  const handleNavigate = useCallback(
    (item: Profile) => {
      if (item.link === "logout") {
        logout();
        handleClose();
      } else {
        go(item.link);
      }
    },
    [logout, go, handleClose],
  );

  const vipProgress = Math.max(0, Math.min(100, Math.round(standing?.progress || 0)));

  const destinations = PROFILE_ITEMS.filter((item) => item.link !== "logout");
  const signOut = PROFILE_ITEMS.find((item) => item.link === "logout");

  // The avatar carries a dot when a row inside the menu is badged.
  const hasUnread = isMobile && unreadCount > 0;
  const spinReady = Boolean(dailySpin?.enabled && dailySpin.available);

  return (
    <>
      <Badge
        variant="dot"
        overlap="circular"
        invisible={!hasUnread && !spinReady}
        // Red for notifications, as on the row; gold when only the spin waits.
        sx={headerBadgeSx(hasUnread ? colors.red : MASCOT_PALETTE.crown)}
      >
        <IconButton
          size="small"
          onClick={handleClick}
          aria-controls={open ? "profile-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          aria-label={t("header.account-menu")}
          sx={{
            overflow: "hidden",
            // The size of the round buttons beside it.
            width: HEADER_BUTTON_SIZE,
            height: HEADER_BUTTON_SIZE,
            flexShrink: 0,
            p: 0,
            "& img": { width: "100%", height: "100%" },
            // A circle: the theme gives icon buttons the rounded-square corners
            // of every other control, which made the avatar a rounded square.
            borderRadius: "50%",
            border: "2px solid",
            borderColor: open ? SKY : "transparent",
            transition: "border-color 160ms ease, transform 120ms ease",
            "&:hover": { borderColor: SKY },
            "&:focus-visible": { outline: `2px solid ${SKY}`, outlineOffset: 2 },
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          <UserAvatar avatar={avatar} />
        </IconButton>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        id="profile-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        MenuListProps={{ sx: { p: 0 } }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 320,
              maxWidth: "calc(100vw - 24px)",
              borderRadius: "16px",
              border: "1px solid",
              borderColor: alpha(colors.prime, 0.25),
              boxShadow: "0 18px 44px rgba(0, 0, 0, 0.35)",
              overflow: "hidden",
            },
          },
        }}
        className={mode}
      >
        {/* Who is signed in, on the blue light the mascot stands in. */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={(theme) => ({
            p: 2,
            // The panel the VIP and offers pages open on, at the head of the
            // menu: cloud white to pale sky in the light scheme, night navy to
            // deep blue in the dark one, with the blue light in its corner.
            borderBottom: "1px solid",
            borderColor: alpha(colors.prime, 0.22),
            background: `radial-gradient(70% 140% at 0% 100%, ${alpha(colors.prime, 0.24)} 0%, transparent 70%), linear-gradient(135deg, ${MASCOT_PALETTE.cloud}, ${MASCOT_PALETTE.mist})`,
            ...theme.applyStyles("dark", {
              borderColor: alpha(SKY, 0.24),
              background: `radial-gradient(70% 140% at 0% 100%, ${alpha(colors.prime, 0.42)} 0%, transparent 70%), linear-gradient(135deg, ${MASCOT_PALETTE.night}, ${MASCOT_PALETTE.deep})`,
            }),
          })}
        >
          <Box
            sx={{
              flexShrink: 0,
              p: "2px",
              borderRadius: "50%",
              border: `2px solid ${SKY}`,
              lineHeight: 0,
            }}
          >
            <UserAvatar avatar={avatar} />
          </Box>
          <Stack sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={(theme) => ({
                fontWeight: 800,
                lineHeight: 1.25,
                // Ink for the panel, not for the page: the panel is dark in the
                // dark scheme and pale in the light one whatever is behind it.
                color: MASCOT_PALETTE.night,
                ...theme.applyStyles("dark", { color: colors.white }),
              })}
            >
              {user?.username}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={(theme) => ({
                color: alpha(MASCOT_PALETTE.night, 0.7),
                ...theme.applyStyles("dark", { color: alpha("#ffffff", 0.72) }),
              })}
            >
              {user?.email}
            </Typography>
          </Stack>
        </Stack>

        {standing?.current && (
          // The player's VIP pass, and the way to the club: the whole card is
          // the link. It follows the scheme like the panel above it: cloud
          // white with navy ink in the light one, night navy with white ink in
          // the dark one, the crown's gold on both. The ring around the badge
          // is the progress to the next level.
          <ButtonBase
            onClick={() => go(routers.VipClubs)}
            sx={(theme) => ({
              display: "block",
              width: "calc(100% - 24px)",
              mx: 1.5,
              mt: 1.5,
              overflow: "hidden",
              textAlign: "left",
              fontFamily: "inherit",
              color: MASCOT_PALETTE.night,
              borderRadius: "14px",
              border: "1px solid",
              borderColor: alpha(CROWN, 0.55),
              background: `linear-gradient(135deg, ${colors.white}, ${MASCOT_PALETTE.cloud})`,
              transition: "border-color 160ms ease, box-shadow 160ms ease",
              "&:hover": {
                borderColor: CROWN,
                boxShadow: `0 6px 18px ${alpha(CROWN, 0.22)}`,
              },
              "&:focus-visible": { outline: `2px solid ${SKY}`, outlineOffset: 2 },
              // The menu is itself navy in the dark scheme: one step lighter.
              ...theme.applyStyles("dark", {
                color: colors.white,
                borderColor: alpha(CROWN, 0.38),
                background: colors.layer2Dark,
                "&:hover": {
                  borderColor: alpha(CROWN, 0.8),
                  boxShadow: `0 6px 18px ${alpha(CROWN, 0.18)}`,
                },
              }),
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            })}
          >
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ p: 1.5 }}>
              <Box sx={{ position: "relative", flexShrink: 0, width: 52, height: 52 }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={52}
                  thickness={3.6}
                  aria-hidden
                  sx={(theme) => ({
                    position: "absolute",
                    inset: 0,
                    color: alpha(MASCOT_PALETTE.night, 0.1),
                    ...theme.applyStyles("dark", { color: alpha(colors.white, 0.14) }),
                  })}
                />
                <CircularProgress
                  variant="determinate"
                  value={standing.next ? vipProgress : 100}
                  size={52}
                  thickness={3.6}
                  aria-label={t("profile.vip-level")}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    color: CROWN,
                    "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                  }}
                />
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    inset: 9,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    backgroundColor: alpha(MASCOT_PALETTE.night, 0.06),
                    ...theme.applyStyles("dark", { backgroundColor: alpha(colors.white, 0.08) }),
                  })}
                >
                  <Box
                    component="img"
                    src={getMediaUrl(standing.current.image) || DEFAULT_AVATAR_SRC}
                    alt=""
                    sx={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                </Box>
              </Box>
              <Stack flex={1} minWidth={0}>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    fontWeight: 800,
                    letterSpacing: 0.7,
                    textTransform: "uppercase",
                    color: CROWN_INK,
                    ...theme.applyStyles("dark", { color: CROWN }),
                  })}
                >
                  {t("profile.vip-level")} {String(standing.current.level).padStart(2, "0")}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                  {standing.current.name}
                </Typography>
              </Stack>
              <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
                >
                  {standing.point.toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: alpha(MASCOT_PALETTE.night, 0.7),
                    ...theme.applyStyles("dark", { color: alpha(colors.white, 0.7) }),
                  })}
                >
                  {t("profile.vip-points").replace(/:\s*$/, "")}
                </Typography>
              </Stack>
            </Stack>

            {/* How far the next level is, on a strip of its own. */}
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={(theme) => ({
                px: 1.5,
                py: 1,
                borderTop: `1px solid ${alpha(MASCOT_PALETTE.night, 0.08)}`,
                backgroundColor: alpha(MASCOT_PALETTE.night, 0.04),
                ...theme.applyStyles("dark", {
                  borderTop: `1px solid ${alpha(colors.white, 0.08)}`,
                  backgroundColor: alpha(colors.white, 0.06),
                }),
              })}
            >
              {standing.next ? (
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    fontWeight: 800,
                    fontVariantNumeric: "tabular-nums",
                    color: CROWN_INK,
                    ...theme.applyStyles("dark", { color: CROWN }),
                  })}
                >
                  {vipProgress}%
                </Typography>
              ) : (
                // The crown marks the top of the ladder, as it does everywhere.
                <Box
                  component={FaCrown}
                  aria-hidden
                  sx={(theme) => ({
                    color: CROWN_INK,
                    ...theme.applyStyles("dark", { color: CROWN }),
                  })}
                />
              )}
              <Typography
                variant="caption"
                noWrap
                sx={(theme) => ({
                  flex: 1,
                  minWidth: 0,
                  color: alpha(MASCOT_PALETTE.night, 0.7),
                  ...theme.applyStyles("dark", { color: alpha(colors.white, 0.7) }),
                })}
              >
                {standing.next
                  ? t("vip-club.to-go", {
                      points: Math.max(0, standing.next.points - standing.point).toLocaleString(),
                      name: standing.next.name,
                    })
                  : t("profile.vip-top-tier")}
              </Typography>
              <MdChevronRight aria-hidden size={18} style={{ flexShrink: 0, opacity: 0.7 }} />
            </Stack>
          </ButtonBase>
        )}

        <Stack sx={{ p: 1, gap: 0.25 }}>
          {isMobile && (
            <MenuRow
              onClick={() => go(routers.Notifications)}
              icon={
                <Badge badgeContent={unreadCount} color="error" overlap="circular" max={99}>
                  <IoNotifications aria-hidden size={18} />
                </Badge>
              }
              label={<Translations text="notification.title" />}
            />
          )}
          {/* Moved here from the header to keep its row of icons short. */}
          <MenuRow
            onClick={handleOpenChatRoom}
            icon={<IoChatbubbles aria-hidden size={18} />}
            label={<Translations text="chat.title" />}
          />
          {dailySpin?.enabled && (
            <MenuRow
              onClick={handleOpenDailySpin}
              icon={
                <Badge
                  variant="dot"
                  overlap="circular"
                  invisible={!dailySpin.available}
                  sx={{ "& .MuiBadge-badge": { backgroundColor: MASCOT_PALETTE.crown } }}
                >
                  <IoGift aria-hidden size={18} />
                </Badge>
              }
              label={<Translations text="header.daily-spin" />}
            />
          )}
          {/* Stays open on press so the player sees the scheme change. */}
          <MenuRow
            onClick={() => setMode(dark ? "light" : "dark")}
            icon={<AppIcon name={dark ? "moon" : "sun"} size={18} />}
            label={<Translations text="header.dark-mode" />}
            trailing={
              <Switch
                size="small"
                checked={dark}
                tabIndex={-1}
                inputProps={{ "aria-hidden": true }}
                sx={{ pointerEvents: "none" }}
              />
            }
          />
          {destinations.map((item) => (
            <MenuRow
              key={item.id}
              onClick={() => handleNavigate(item)}
              icon={<AppIcon name={item.icon} size={18} />}
              label={<Translations text={item.title} />}
            />
          ))}
        </Stack>

        {signOut && [
          <Divider key="signout-divider" />,
          <Box key="signout-box" sx={{ p: 1 }}>
            <MenuRow
              danger
              onClick={() => handleNavigate(signOut)}
              icon={<AppIcon name={signOut.icon} size={18} />}
              label={<Translations text={signOut.title} />}
            />
          </Box>,
        ]}
      </Menu>
    </>
  );
}
